import type { CaptureResult, PostHogConfig } from 'posthog-js';
import type { App } from 'vue';
import posthog from 'posthog-js';
import { usesLocalFeatureFlags } from '@/composables/localFeatureFlags';
import { applyCspNonce } from '@/helpers/cspNonce';
import { containsPlatformOauthSecret, isPlatformOauthApiUrl, redactPlatformOauthNested } from '@/helpers/platformOauthTelemetry';

const posthogCredentials = (): { apiKey: string; apiHost: string } | null => {
    const apiKey: string | undefined = import.meta.env.VITE_POSTHOG_API_KEY;
    const apiHost: string | undefined = import.meta.env.VITE_POSTHOG_API_HOST;

    if (apiKey == null || apiKey === '' || apiHost == null || apiHost === '') return null;

    return { apiKey, apiHost };
};

// Nothing initialises PostHog without these (local dev, tests), so no feature flags will
// ever load and a reader waiting on them must not wait at all.
export const isPostHogConfigured = (): boolean => posthogCredentials() !== null;

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

// rrweb event types whose small, uncompressed payloads carry URLs: Meta (page href), Custom
// (PostHog's $url_changed/$pageview href) and Plugin (the network recorder's requests).
const urlBearingRecordingEventTypes = [4, 5, 6];

const withoutPlatformOauthRequests = (data: Record<string, unknown>): Record<string, unknown> => {
    const { payload } = data;
    if (!isRecord(payload) || !Array.isArray(payload.requests)) return data;

    return {
        ...data,
        payload: {
            ...payload,
            requests: payload.requests.filter((request: unknown) => !(isRecord(request)
                && typeof request.name === 'string'
                && isPlatformOauthApiUrl(request.name))),
        },
    };
};

const redactRecordingEvent = (recordingEvent: unknown): unknown => {
    if (!isRecord(recordingEvent) || !isRecord(recordingEvent.data) || typeof recordingEvent.type !== 'number'
        || !urlBearingRecordingEventTypes.includes(recordingEvent.type)) {
        return recordingEvent;
    }

    return redactPlatformOauthNested({ ...recordingEvent, data: withoutPlatformOauthRequests(recordingEvent.data) });
};

// Snapshots are redacted per rrweb event so DOM snapshots, which never show the pending ID, are
// not serialised on every recording flush.
const redactPlatformOauthCapture = (capture: CaptureResult | null): CaptureResult | null => {
    if (capture === null) return null;

    if (capture.event === '$snapshot') {
        const snapshotData: unknown = capture.properties.$snapshot_data;

        return {
            ...capture,
            properties: {
                ...capture.properties,
                $snapshot_data: Array.isArray(snapshotData) ? snapshotData.map(redactRecordingEvent) : redactRecordingEvent(snapshotData),
            },
        };
    }

    return {
        ...capture,
        properties: redactPlatformOauthNested(capture.properties),
        $set: redactPlatformOauthNested(capture.$set),
        $set_once: redactPlatformOauthNested(capture.$set_once),
    };
};

// PostHog stores the current URL, masked by these options, before before_send sees any event:
// as the landing URL at init and as the session entry URL whenever a new session starts. Only
// consent URLs (and login URLs returning to one) opt in because the option also masks the ad
// click IDs that other pages report.
const platformOauthMasking = (url: string): Pick<PostHogConfig, 'mask_personal_data_properties' | 'custom_personal_data_properties'> => {
    const masked = containsPlatformOauthSecret(url);

    return {
        mask_personal_data_properties: masked,
        custom_personal_data_properties: masked ? ['authorization_request_id', 'redirect'] : [],
    };
};

const applyPlatformOauthMasking = (url: string): void => {
    const masking = platformOauthMasking(url);
    if (posthog.config.mask_personal_data_properties !== masking.mask_personal_data_properties) {
        posthog.set_config(masking);
    }
};

// A session can start at any point while the browser shows the consent URL, so masking is
// switched on before an SPA navigation onto it commits and only switched off once the browser
// has actually left it.
const maskPlatformOauthNavigations = (app: App): void => {
    const router = app.config.globalProperties.$router;

    router.beforeEach((to) => {
        if (containsPlatformOauthSecret(to.fullPath)) {
            applyPlatformOauthMasking(to.fullPath);
        }
    });
    router.afterEach(() => applyPlatformOauthMasking(window.location.href));
};

export default {
    install(app: App): void {
        const credentials = posthogCredentials();

        if (credentials !== null) {
            posthog.init(credentials.apiKey, {
                api_host: credentials.apiHost,
                autocapture: false,
                capture_pageview: false,
                capture_pageleave: false,
                cross_subdomain_cookie: false,
                disable_compression: true,
                enable_recording_console_log: true,
                opt_in_site_apps: true,
                // Analytics can still be delivered when flags resolve locally; PostHog's flags
                // would only be ignored, so they are not requested.
                advanced_disable_feature_flags: usesLocalFeatureFlags(),
                ...platformOauthMasking(window.location.href),
                before_send: redactPlatformOauthCapture,
                prepare_external_dependency_script: (script: HTMLScriptElement): HTMLScriptElement => {
                    applyCspNonce(script);

                    return script;
                },
                prepare_external_dependency_stylesheet: (stylesheet: HTMLStyleElement): HTMLStyleElement => {
                    applyCspNonce(stylesheet);

                    return stylesheet;
                },
                // maskCapturedNetworkRequestFn would also reach recorded URLs, but defining it
                // switches off PostHog's default payload scrubbing for every other request, so
                // recordings are redacted in before_send instead.
                session_recording: {
                    recordCrossOriginIframes: false,
                    blockSelector: 'iframe',
                },
            });
            maskPlatformOauthNavigations(app);
        }
    },
};
